import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import type { ChatMessage } from '@/hooks/useChatConversation'
import { chatSourceDestination, type SessionOption, type SessionPage } from '@/services/chatbotService'
import { SessionContactCard } from './SessionContactCard'

export function ChatMessageBubble({ message, onNavigate, onSelectSession, onShowMore, selectionDisabled }: {
  message: ChatMessage
  onNavigate: () => void
  onSelectSession?: (session: SessionOption, label: string) => void
  onShowMore?: (page: SessionPage) => void
  selectionDisabled?: boolean
}) {
  const { t, i18n } = useTranslation('dashboard')
  const isUser = message.role === 'user'

  return (
    <div
      data-message-id={message.id}
      className={`mb-4 flex ${
        isUser ? 'justify-end' : 'justify-start'
      }`}
    >
      <div
        className={`min-w-0 [overflow-wrap:anywhere] rounded-2xl px-3 sm:px-4 py-3 ${
          isUser
            ? 'max-w-[85%] bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-tr-sm shadow-md'
            : 'max-w-full sm:max-w-[92%] bg-white text-gray-800 shadow-md border border-gray-200 rounded-tl-sm'
        }`}
      >
        <p className="text-sm whitespace-pre-line leading-relaxed">{message.content}</p>

        {message.response?.sessionOptions?.map(session => {
          // Catalog dates are wall-clock values. Preserve their stored time rather than
          // shifting the displayed session time to the visitor's timezone.
          const date = (value: string) => new Date(value.replace(/(?:Z|[+-]\d{2}:\d{2})$/, ''))
          const locale = i18n.resolvedLanguage || i18n.language
          const day = (value: string) => date(value).toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' })
          const time = (value: string) => date(value).toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' })
          const start = session.startDate
          const end = session.endDate
          const schedule = start ? `${day(start)} · ${time(start)}${end ? `–${day(start) === day(end) ? '' : `${day(end)} · `}${time(end)}` : ''}` : t('chatBotScheduleUnavailable')
          const location = [session.siteName, session.roomName].filter(Boolean).join(' · ') || t('chatBotLocationUnavailable')
          const label = `${schedule} · ${location}`
          return (
            <button key={session.sessionId} type="button" disabled={selectionDisabled || !onSelectSession}
              onClick={() => onSelectSession?.(session, label)}
              className="mt-3 block min-h-11 w-full rounded-lg border border-primary-300 bg-primary-50 px-3 py-2 text-left text-sm text-primary-800 hover:bg-primary-100 focus-visible:outline-2 focus-visible:outline-primary-500 disabled:opacity-50">
              <span className="block font-semibold">{session.title}</span>
              <span className="block">{label}</span>
              <span className="block text-xs">{t('chatBotSessionId', { id: session.sessionId })}</span>
            </button>
          )
        })}

        {message.response?.nextSessionPage && (
          <button type="button" disabled={selectionDisabled || !onShowMore}
            onClick={() => onShowMore?.(message.response!.nextSessionPage!)}
            className="mt-3 min-h-11 rounded-lg border border-primary-300 px-3 py-2 text-sm font-semibold text-primary-700 hover:bg-primary-50 disabled:opacity-50">
            {t('chatBotShowMore')}
          </button>
        )}

        {message.response?.sessionContacts?.map((contact, index) => (
          <SessionContactCard key={`${contact.sessionId}-${index}`} contact={contact} />
        ))}

        {message.response?.sources && message.response.sources.length > 0 && (
          <ul className="mt-3 space-y-2">
            {message.response.sources.map((source, index) => {
              const destination = chatSourceDestination(source)
              if (!destination) return null
              const className = 'block bg-primary-500 hover:bg-primary-600 text-white text-xs font-medium px-3 py-2 rounded-lg transition-all hover:shadow-md'
              return (
                <li key={`${source.id}-${index}`}>
                  {destination === 'internal' ? (
                    <Link to={source.url} onClick={onNavigate} className={className}>{source.title}</Link>
                  ) : (
                    <a href={source.url} target="_blank" rel="noopener noreferrer" className={className}>
                      {source.kind === 'video' && <span>{t('chatBotVideo')}: </span>}
                      {source.title}<span className="sr-only"> {t('chatBotNewTab')}</span>
                    </a>
                  )}
                  {source.description && <p className="text-xs mt-1 text-gray-600">{source.description}</p>}
                </li>
              )
            })}
          </ul>
        )}
        {message.response?.warnings?.map((warning, index) => (
          <p key={index} className="text-xs text-amber-800 mt-2">{warning}</p>
        ))}

        <p
          className={`text-xs mt-1 ${
            isUser ? 'text-primary-100' : 'text-gray-500'
          }`}
        >
          {message.timestamp.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </p>
      </div>
    </div>
  )
}
